import moment from "moment";
import { UPLOAD_URL, WEB_URL } from "../config";

export function formatImage(filename = "") {
  if (filename) {
    if (
      filename.startsWith("http://") ||
      filename.startsWith("https://") ||
      filename.startsWith("blob:") ||
      filename.startsWith("data:")
    ) {
      return filename;
    }

    const baseUrl = (UPLOAD_URL || "").replace(/\/+$/, "");
    const normalizedFilename = filename.replace(/^\/+/, "");
    return `${baseUrl}/${normalizedFilename}`;
  }
}

export function formatTextWithLink(description: string = "") {
  return description.replace(
    /(?:https?|http):\/\/[^\s<>]+/g,
    (url) =>
      `<a href="${url}" target="_blank" style="color: #30f5ff; text-decoration: underline" rel="noreferrer" >${url}</a>`,
  );
}

export function formatDate(date: string = "", format = "DD MMM, YYYY") {
  return moment(date).isValid()
    ? moment.utc(date).utcOffset(330).format(format)
    : "?";
}

export function formatTime(time?: string, format = "hh:mm A") {
  return moment(time).isValid()
    ? moment.utc(time).utcOffset(330).format(format)
    : "?";
}

/**
 * Display any API timestamp in IST (UTC+05:30). Returns em dash when invalid.
 *
 * Backend pg parser + API now emit absolute instants (ISO with `Z`).
 * Naive strings (no zone) are treated as IST wall-clock for legacy payloads.
 */
export function formatDateTimeIst(
  date?: string | Date | null,
  format = "DD MMM YYYY, hh:mm A",
): string {
  if (date == null || date === "") return "—";

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) return "—";
    return moment(date).utcOffset(330).format(format);
  }

  const raw = String(date).trim();
  if (!raw) return "—";

  if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(raw)) {
    const m = moment.parseZone(raw);
    return m.isValid() ? m.utcOffset(330).format(format) : "—";
  }

  // Legacy naive text — IST wall-clock (do not append Z)
  const naive = moment(raw.replace(" ", "T"));
  return naive.isValid() ? naive.format(format) : "—";
}

/**
 * DateTimePicker value `YYYY-MM-DDTHH:mm` → ISO UTC, interpreting the wall clock as IST
 * (so booking/status times match India regardless of browser timezone).
 */
export function istPickerValueToIso(value: string): string {
  if (!value) return "";
  const m = moment(value, "YYYY-MM-DDTHH:mm", true);
  if (!m.isValid()) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toISOString();
  }
  return m.utcOffset(330, true).toISOString();
}

export interface ScheduleDateTimeDisplay {
  dateLine: string;
  timeLine: string;
}

/** Formats separate schedule date + time fields for table display. Returns null if either value is missing or invalid. */
export function formatScheduleDateTime(
  date?: string | null,
  time?: string | null,
): ScheduleDateTimeDisplay | null {
  if (!date?.trim() || !time?.trim()) return null;

  const dateMoment = moment(date);
  const timeMoment = moment(time, ["HH:mm:ss", "HH:mm"], true);

  if (!dateMoment.isValid() || !timeMoment.isValid()) return null;

  return {
    dateLine: dateMoment.format("DD MMM YYYY"),
    timeLine: timeMoment.format("hh:mm A"),
  };
}

/** Returns a Unix timestamp for schedule date + time fields. Returns null if either value is missing or invalid. */
export function getScheduleTimestamp(date?: string | null, time?: string | null): number | null {
  if (!date?.trim() || !time?.trim()) return null;

  const dateMoment = moment(date);
  const timeMoment = moment(time, ["HH:mm:ss", "HH:mm"], true);

  if (!dateMoment.isValid() || !timeMoment.isValid()) return null;

  const combined = moment(
    `${dateMoment.format("YYYY-MM-DD")} ${timeMoment.format("HH:mm:ss")}`,
    "YYYY-MM-DD HH:mm:ss",
  );

  return combined.isValid() ? combined.valueOf() : null;
}

export function formatSlug(text: string) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~]/g, "") // remove common punctuation
    .replace(/\s+/g, "-") // Replace all spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with a single hyphen
    .replace(/^-+|-+$/g, "");
}

export function formatArticleUrl(slug: string) {
  return WEB_URL + "/articles/" + slug;
}
